/**
 * Task 10 (frontend half) — the claim's running commentary.
 *
 * The BACKEND half ships alongside this: `POST /claims/{id}/add_note/` writes a
 * `ClaimActivityLog` with `action='note_added'`, and every activity row now
 * carries `is_customer_visible`. `note_added` had been declared in
 * `ClaimActivityLog.ACTION_CHOICES` since the model was written and the claims
 * app never wrote one, so until now the ONLY way a broker could say anything
 * about a claim was to move its status.
 *
 * WHY THIS LIVES UNDER pages/claims/ AND NOT services/claims.ts
 * -------------------------------------------------------------
 * Same reason as `claimDocuments.ts` next door: `services/claims.ts`,
 * `hooks/useClaims.ts` and `types/index.ts` are all outside this task's file
 * domain. `claimService.updateStatus` already accepts a `notes` argument (the
 * UI simply never passed one), so the stage-change description needs nothing
 * new there; only the add-note call and the timeline row's new field do. See
 * the handover note about folding this back into `services/claims.ts`.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import api from '@/services/api';
import { getErrorMessage } from '@/services';

/**
 * One row of `GET /claims/{id}/timeline/`, as `ClaimActivitySerializer` sends
 * it.
 *
 * NOT `types/index.ts`'s `ClaimTimeline`, which is wrong in two ways: it lacks
 * `is_customer_visible`, and it declares `old_status`/`new_status` as required
 * fields that the serializer has never sent (which is why the status Badge in
 * the tracking card has never once rendered). Correcting that shared type is
 * outside this task's domain — see the report.
 */
export interface ClaimTimelineEntry {
  id: string;
  action: string;
  description: string;
  performed_by_name?: string | null;
  performed_by_role?: string;
  /**
   * Whether the claimant can read this entry. Absent on a response from a
   * server that predates task 10, and `undefined` must NOT be read as "shared"
   * — `isSharedWithCustomer` below treats only an explicit `true` as shared.
   */
  is_customer_visible?: boolean;
  new_status?: string | null;
  created_at: string;
}

/** Only an explicit `true` counts as shared. See `is_customer_visible` above. */
export function isSharedWithCustomer(entry: ClaimTimelineEntry): boolean {
  return entry.is_customer_visible === true;
}

/**
 * Statuses whose description the SERVER publishes to the claimant whatever the
 * checkbox says, because `ClaimService.update_claim_status` copies the reason
 * onto `claim.rejection_reason` and `ClaimDetailSerializer` serves that field.
 * Mirrors `ClaimViewSet.PUBLISHED_REASON_STATUSES`; the UI shows the checkbox
 * ticked and disabled rather than letting a broker believe a rejection reason
 * is private.
 */
export const PUBLISHED_REASON_STATUSES = ['rejected'];

export interface AddClaimNoteInput {
  note: string;
  isCustomerVisible: boolean;
}

/**
 * Statuses whose transition CAPTURES an amount. B7.
 *
 * Mirrors `ClaimStatusUpdateSerializer.APPROVAL_STATUSES` /
 * `SETTLEMENT_STATUSES`; the server requires the matching figure on these hops
 * and REFUSES it on every other one, so the modal must ask for exactly the same
 * set or the broker meets a 400 they cannot act on.
 *
 * `partially_approved` is in the approval list deliberately: it is the SECOND
 * path into settlement (`ClaimService.STATUS_TRANSITIONS`), not a dead end.
 * `settled` and not `settlement_pending`: the graph is
 * `approved -> settlement_pending -> settled`, so the settled figure belongs on
 * the last hop.
 */
export const APPROVAL_AMOUNT_STATUSES = ['approved', 'partially_approved'];
export const SETTLEMENT_AMOUNT_STATUSES = ['settled'];

export interface UpdateClaimStatusInput {
  status: string;
  /** The broker's description of WHY the claim moved. Optional. */
  note: string;
  isCustomerVisible: boolean;
  /** B7. Sent ONLY on a transition into approved/partially_approved. */
  approvedAmount?: string;
  /** B7. Sent ONLY on a transition into settled. */
  settledAmount?: string;
}

export const claimNotesApi = {
  async add(claimId: string, input: AddClaimNoteInput): Promise<ClaimTimelineEntry> {
    const response = await api.post<ClaimTimelineEntry>(`/claims/${claimId}/add_note/`, {
      note: input.note,
      is_customer_visible: input.isCustomerVisible,
    });
    return response.data;
  },

  /**
   * `POST /claims/{id}/update_status/` WITH the description and its visibility.
   *
   * WHY NOT `claimService.updateStatus` / `useUpdateClaimStatus`: that function
   * is `(id, status, notes)` and posts exactly `{status, notes}`. There is no
   * third thing it can carry, and TypeScript rejects the extra property rather
   * than passing it, so routing the visibility flag through it is not merely
   * lossy — it does not compile. Widening it means editing
   * `services/claims.ts` and `hooks/useClaims.ts`, both outside this task's
   * file domain. This duplicate should be deleted and folded back into
   * `useUpdateClaimStatus` the moment that file is in scope; until then the two
   * must be kept in step (same endpoint, same invalidations).
   */
  async updateStatus(claimId: string, input: UpdateClaimStatusInput): Promise<unknown> {
    const body: Record<string, unknown> = {
      status: input.status,
      notes: input.note,
      is_customer_visible: input.isCustomerVisible,
    };
    // B7: added to the body only when the transition is one that takes it. The
    // server REFUSES an Approved Amount on a rejection and a Settled Amount on
    // anything but the settlement -- rather than accepting the figure and
    // dropping it -- so posting an empty key here would turn a legal transition
    // into a 400.
    if (input.approvedAmount !== undefined && APPROVAL_AMOUNT_STATUSES.includes(input.status)) {
      body.approved_amount = input.approvedAmount;
    }
    if (input.settledAmount !== undefined && SETTLEMENT_AMOUNT_STATUSES.includes(input.status)) {
      body.settled_amount = input.settledAmount;
    }
    const response = await api.post(`/claims/${claimId}/update_status/`, body);
    return response.data;
  },
};

/**
 * Move a claim to its next stage, carrying the broker's description.
 *
 * Invalidations mirror `useUpdateClaimStatus` in `hooks/useClaims.ts` exactly —
 * see the note on `claimNotesApi.updateStatus` about why this exists at all.
 */
export function useUpdateClaimStatusWithNote(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateClaimStatusInput) => claimNotesApi.updateStatus(claimId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['claims'] });
      queryClient.invalidateQueries({ queryKey: ['claim', claimId] });
      queryClient.invalidateQueries({ queryKey: ['claim-timeline', claimId] });
      toast.success('Claim status updated');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

/**
 * Add a note to a claim without changing its stage.
 *
 * Invalidates the timeline AND the claim detail: the detail payload embeds the
 * same activity rows under `activity_logs`, so leaving it cached would show a
 * stale history the moment anything read from there.
 */
export function useAddClaimNote(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: AddClaimNoteInput) => claimNotesApi.add(claimId, input),
    onSuccess: (entry) => {
      queryClient.invalidateQueries({ queryKey: ['claim-timeline', claimId] });
      queryClient.invalidateQueries({ queryKey: ['claim', claimId] });
      toast.success(
        isSharedWithCustomer(entry) ? 'Note added and shared with the customer' : 'Internal note added'
      );
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}
